<template>
  <div class="dashboard-pages">
    <v-card class="pa-3">
      <v-layout row wrap justify-space-between>
        <dt-search-box
          :search="search"
          @typing="searchValue"
          @reset-datatable="resetDataTable()"
          @clear-search-field="search = null"
        />
        <v-layout row wrap justify-end class="pt-2 pb-2">
          <v-btn
            dark
            color="primary"
            v-if="hasPermissions(['weights_auditor_store'])"
            @click="dialogs.addDialog = true"
          >
            <v-icon>add</v-icon>
            {{ $t('datatable-add-new') }}
          </v-btn>
        </v-layout>
      </v-layout>
    </v-card>

    <v-card flat>
      <v-layout row wrap justify-end>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 0px; height: 30px; width: 30px"
              :loading="loadThePrintBtn"
              flat
              fab
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('print')"
            >
              <v-icon>print</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Print Table`) }}</span>
        </v-tooltip>
        <v-tooltip :left="!$vuetify.rtl" :right="$vuetify.rtl">
          <template v-slot:activator="{ on }">
            <v-btn
              style="margin: 5px 20px 5px 10px; height: 30px; width: 30px"
              fab
              :loading="loadTheExportBtn"
              flat
              v-on="on"
              color="primary"
              @click="getNoPaginatedData('export')"
            >
              <v-icon>fa-file-excel</v-icon>
            </v-btn>
          </template>
          <span>{{ $t(`Export Table`) }}</span>
        </v-tooltip>
      </v-layout>
    </v-card>

    <v-card class="px-4 pb-4">
      <v-data-table
        v-show="isDatatable"
        :headers="headers"
        :items="resultData.data"
        :item-key="defaultSortBy"
        :search="search"
        :loading="loading"
        :pagination.sync="pagination"
        :rows-per-page-items="pagination.rowsPerPageItems"
        :total-items="resultData.total"
        :no-data-text="$t('No available data')"
        hide-actions
      >
        <template slot="items" slot-scope="props">
          <!--          <td>{{ props.item.weight_auditor_id }}</td>-->
          <td>{{ props.item.factory_name }}</td>
          <td>{{ props.item.user_full_name }}</td>
          <td>{{ $t(props.item.type) }}</td>
          <td>{{ $t(props.item.shift_time) }}</td>
          <td>{{ props.item.date }}</td>
          <td v-if="hasActionsMenuPermissions()">
            <v-menu offset-y bottom :left="!$vuetify.rtl" :right="$vuetify.rtl" transition="slide-y-reverse-transition">
              <v-btn flat fab icon class="transparent py-2" slot="activator">
                <v-icon large class="grey--text actions-btn">more_vert</v-icon>
              </v-btn>
              <v-list>
                <v-tooltip v-if="hasPermissions(['weights_auditor_show'])" :left="!$vuetify.rtl" :right="$vuetify.rtl">
                  <v-list-tile slot="activator" @click="viewRecord(props.item.weight_auditor_id)">
                    <v-icon>chrome_reader_mode</v-icon>
                    <v-list-tile-content class="px-3">
                      {{ $t('datatable-view') }}
                    </v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`View Weight Auditor's information`) }}</span>
                </v-tooltip>

                <v-tooltip
                  v-if="hasPermissions(['weights_auditor_update'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="editRecord(props.item.weight_auditor_id)">
                    <v-icon>edit</v-icon>
                    <v-list-tile-content class="px-3">
                      {{ $t('datatable-edit') }}
                    </v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t(`Edit Weight Auditor's information`) }}</span>
                </v-tooltip>

                <v-divider v-if="hasPermissions(['weights_auditor_destroy'])" />

                <v-tooltip
                  v-if="hasPermissions(['weights_auditor_destroy'])"
                  :left="!$vuetify.rtl"
                  :right="$vuetify.rtl"
                >
                  <v-list-tile slot="activator" @click="showConfirmDeletion(props.item.weight_auditor_id)">
                    <v-icon class="red--text">delete</v-icon>
                    <v-list-tile-content class="red--text px-3">
                      {{ $t('datatable-delete') }}
                    </v-list-tile-content>
                  </v-list-tile>
                  <span>{{ $t('Delete Weight Auditor Record') }}</span>
                </v-tooltip>
              </v-list>
            </v-menu>
          </td>
        </template>

        <v-alert slot="no-results" :value="true" color="error" icon="warning">
          {{ $t('Sorry, No results found for your search criteria') }}
        </v-alert>
      </v-data-table>

      <dt-pagination
        :resultData="resultData"
        :pagination="pagination"
        :perPageNumber="perPageNumber"
        @page-changed="pageChanged"
        @per-page-changed="resultsPerPageChanged"
      />
    </v-card>

    <add-weight-auditor
      :addDialog="dialogs.addDialog"
      @reset-datatable="resetDataTable(), resetAllErrors(true)"
      @hidemodal="resetAllErrors(true), (dialogs.addDialog = false)"
    />

    <edit-other-weight-auditor
      :editDialogOther="dialogs.editDialogOther"
      @updated="updateDatatableResult(), (dialogs.editDialogOther = false), (allowGettingData = false)"
      @hidemodal="resetAllErrors(true), (dialogs.editDialogOther = false)"
    />

    <edit-agricultural-weight-auditor
      :editDialogAgricultural="dialogs.editDialogAgricultural"
      @updated="updateDatatableResult(), (dialogs.editDialogAgricultural = false), (allowGettingData = false)"
      @hidemodal="resetAllErrors(true), (dialogs.editDialogAgricultural = false)"
    />

    <confirm-deletion
      :deleteDialog="dialogs.deleteDialog"
      :btnDeleteLoading="btnDeleteLoading"
      @hidemodal="dialogs.deleteDialog = false"
      @confirm="deleteAction()"
    />

    <view-agricultural-weight-auditor
      v-if="!hideViewDialogAgricultural"
      :viewDialogAgricultural="dialogs.viewDialogAgricultural"
      @hidemodal="dialogs.viewDialogAgricultural = false"
      @hideother="hideViewDialogOther = true"
      @showother="hideViewDialogOther = false"
    />

    <view-other-weight-auditor
      v-if="!hideViewDialogOther"
      :viewDialogOther="dialogs.viewDialogOther"
      @hidemodal="dialogs.viewDialogOther = false"
      @hideagricultural="hideViewDialogAgricultural = true"
      @showagricultural="hideViewDialogAgricultural = false"
    />

    <print-export-component
      :printExportDialog="dialogs.printExportDialog"
      :tableHeader="tableHeader"
      :tableBody="tableBody"
      :clickedButton="clickedButton"
      @hidemodal="dialogs.printExportDialog = false"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex'
import * as helpers from '@/mixins/helperFunctions.js'
import Axios from 'axios'

export default {
  components: {
    dtSearchBox: () => import('@/views/_partials/AppSearchBox.vue'),
    dtPagination: () => import('@/views/_partials/AppDatatablePagination.vue'),
    printExportComponent: () => import('@/views/_partials/printExportComponent.vue'),
    addWeightAuditor: () => import('./addWeightAuditor.vue'),
    editAgriculturalWeightAuditor: () => import('./editAgriculturalWeightAuditor.vue'),
    editOtherWeightAuditor: () => import('./editOtherWeightAuditor.vue'),
    viewAgriculturalWeightAuditor: () => import('./viewAgriculturalWeightAuditor.vue'),
    viewOtherWeightAuditor: () => import('./viewOtherWeightAuditor.vue')
  },

  data() {
    return {
      dialogs: {
        viewDialogAgricultural: false,
        viewDialogOther: false,
        addDialog: false,
        editDialogOther: false,
        editDialogAgricultural: false,
        deleteDialog: false,
        printExportDialog: false
      },
      tableBody: [],
      tableHeader: [],
      loadThePrintBtn: false,
      loadTheExportBtn: false,
      clickedButton: '',
      hideViewDialogAgricultural: false,
      hideViewDialogOther: false,
      deleteId: 0,
      btnDeleteLoading: false,
      isDatatable: true,
      search: null,
      oldSearch: null,
      loading: false,
      defaultSortBy: 'weight_auditor_id',
      perPageNumber: 10,
      pagination: {
        sortBy: this.defaultSortBy,
        descending: true,
        page: 1,
        rowsPerPage: 10,
        totalItems: 1
      },
      oldPagination: {},
      headers: [
        // {
        //   text: this.$t('ID'),
        //   value: 'weight_auditor_id',
        //   align: 'left',
        //   sortable: true
        // },
        { text: this.$t('Factory'), value: 'factory_name' },
        { text: this.$t('Staff'), value: 'user_full_name' },
        { text: this.$t('Type'), value: 'type' },
        { text: this.$t('Shift Time'), value: 'shift_time' },
        { text: this.$t('Date'), value: 'date' }
      ],
      allowGettingData: true
    }
  },

  mounted() {
    this.setAppBarTitle({
      title: this.$t('Weight Auditors'),
      icon: 'fa-dolly'
    })

    this.setFullFactoriesList()

    if (this.hasActionsMenuPermissions()) {
      this.headers.push({
        text: this.$t('datatable-actions'),
        value: '',
        sortable: false
      })
    }
  },

  computed: {
    ...mapGetters({
      resultData: 'weightAuditors/getWeightAuditorList',
      weightAuditorInfo: 'weightAuditors/getWeightAuditorInfo'
    })
  },

  watch: {
    pagination: {
      immediate: true,
      deep: true,
      handler() {
        let p = this.pagination
        let op = this.oldPagination
        if (p.sortBy !== op.sortBy || p.descending !== op.descending) {
          op.sortBy = p.sortBy
          op.descending = p.descending
          p.page = 1
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        } else if (this.allowGettingData || this.search !== this.oldSearch) {
          this.oldSearch = this.search
          this.updateDatatableResult()
          this.allowGettingData = !this.allowGettingData
        }
      }
    }
  },

  methods: {
    ...mapActions({
      resetAllErrors: 'errors/resetAllErrors',
      setErrorsList: 'errors/setErrorsList',
      updateResult: 'weightAuditors/updateWeightAuditorList',
      deleteRecord: 'weightAuditors/deleteWeightAuditor',
      fetchWeightAuditorInfo: 'weightAuditors/fetchWeightAuditorInfo',
      setFullFactoriesList: 'factories/setFullFactoriesList'
    }),

    hasPermissions(permissions) {
      return helpers.userHasPermissions(permissions)
    },

    hasActionsMenuPermissions() {
      return helpers.userHasPermissions(['weights_auditor_show', 'weights_auditor_update', 'weights_auditor_destroy'])
    },

    getNoPaginatedData(clickedButton) {
      this.clickedButton = clickedButton
      if (clickedButton === 'print') {
        this.loadThePrintBtn = true
      } else if (clickedButton === 'export') {
        this.loadTheExportBtn = true
      }
      this.tableBody = []
      this.tableHeader = []
      this.tableHeader = this.headers
      this.tableHeader = this.tableHeader.filter(f => f.text !== 'Actions' && f.text !== 'الاجراءات')

      Axios.get('weights_auditor_index', {
        params: { is_paginated: 0, search: this.search }
      })
        .then(response => {
          this.tableBody = response.data.data
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = true
        })
        .catch(() => {
          this.loadThePrintBtn = false
          this.loadTheExportBtn = false
          this.dialogs.printExportDialog = false
        })
    },

    updateDatatableResult() {
      this.loading = true
      let payload = { ...this.pagination, search: this.search }
      this.updateResult(payload)
        .then(() => {
          this.pagination.page = this.resultData.current_page
          this.loading = false
        })
        .catch(() => {
          this.loading = false
        })
    },

    resetDataTable() {
      this.allowGettingData = true
      this.pagination = {
        sortBy: null,
        descending: true,
        page: 1,
        rowsPerPage: this.perPageNumber,
        totalItems: 1
      }
      this.search = this.oldSearch = null
    },

    resultsPerPageChanged(val) {
      this.perPageNumber = val
      this.pagination.rowsPerPage = val
      this.pagination.page = 1
      this.updateDatatableResult()
    },

    pageChanged(val) {
      this.pagination.page = val
      this.updateDatatableResult()
    },

    searchValue(val) {
      this.search = val
    },

    viewRecord(id) {
      this.fetchWeightAuditorInfo(id).then(() => {
        if (this.weightAuditorInfo.type === 'OTHER_PRODUCTS') {
          this.dialogs.viewDialogOther = true
        } else {
          this.dialogs.viewDialogAgricultural = true
        }
      })
    },

    editRecord(id) {
      this.fetchWeightAuditorInfo(id).then(() => {
        if (this.weightAuditorInfo.type === 'OTHER_PRODUCTS') {
          this.dialogs.editDialogOther = true
        } else {
          this.dialogs.editDialogAgricultural = true
        }
      })
    },

    showConfirmDeletion(id) {
      this.deleteId = id
      this.dialogs.deleteDialog = true
    },

    deleteAction() {
      this.btnDeleteLoading = true
      this.deleteRecord(this.deleteId)
        .then(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
          let payload = { ...this.pagination, search: this.search }
          this.updateResult(payload)
        })
        .catch(() => {
          this.deleteId = 0
          this.btnDeleteLoading = false
          this.dialogs.deleteDialog = false
        })
    }
  }
}
</script>

<style scoped />
